import React, { useState } from "react";
import { toast } from "sonner";
import { MailWarning, Send } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Bandeau persistant affiché tant que l'e-mail n'est pas confirmé.
 *
 * Objectif produit : un artisan qui s'inscrit au comptoir d'un fournisseur ne
 * verra pas le mail de vérification tout de suite. Sans indicateur visible,
 * il croit son compte actif alors que :
 *   - ses attestations sont bloquées (documents/user/:id renvoie 403)
 *   - il ne reçoit aucune notification e-mail (drop silencieux)
 * Le bandeau reste tant que ``user.email_verified === false`` et propose
 * de renvoyer le lien (rate-limité à 60 s côté serveur).
 */
export default function EmailVerificationBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  if (!user || user.email_verified !== false) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.already_verified) {
        await refresh();
        toast.success("Votre e-mail est déjà confirmé.");
        return;
      }
      toast.success(`Un nouveau lien a été envoyé à ${user.email}.`);
      // Cooldown local (60 s) pour dissuader les clics répétés
      setCooldown(60);
      const t = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) { clearInterval(t); return 0; }
          return c - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-[4px] border border-[#E8710A] bg-[#FDECDD] p-4 md:p-5 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4"
      role="status"
      aria-live="polite"
      data-testid="email-verification-banner"
    >
      <div className="w-9 h-9 rounded-[4px] bg-[#E8710A] text-white flex items-center justify-center shrink-0">
        <MailWarning size={18} strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm md:text-base font-bold text-[#1F2933] leading-snug">
          Votre e-mail n'est pas encore confirmé.
        </p>
        <p className="mt-1 text-sm text-[#3E4C59] leading-relaxed">
          Un lien de confirmation a été envoyé à{" "}
          <span
            className="font-semibold text-[#1F2933] break-all"
            data-testid="email-verification-banner-email"
          >
            {user.email}
          </span>
          . Tant que ce n'est pas fait, les entreprises ne pourront pas consulter vos attestations et vous ne recevrez pas nos e-mails.
        </p>
      </div>
      <button
        type="button"
        onClick={resend}
        disabled={busy || cooldown > 0}
        className="btn-primary shrink-0 min-h-[44px] px-4 text-sm disabled:opacity-60"
        data-testid="email-verification-resend-btn"
      >
        <Send size={16} />
        {busy ? "Envoi…" : cooldown > 0 ? `Renvoyer (${cooldown} s)` : "Renvoyer le lien"}
      </button>
    </div>
  );
}
